import Operations from './Util/Operations';
import Coords, { PixelToHexVector } from './Util/Coords';
import Mouse from './Util/Mouse';
import ToolProps from './Util/ToolProps';
import Keyboard from '../../../services/keyboardShortcuts';

import GridMode from './Shapes/GridMode';
import Ellipse from './Shapes/Ellipse';
import ImageDraw from './Shapes/ImageDraw';
import Rectangle from './Shapes/Rectangle';
import Polygon from './Shapes/Polygon';
import Text from './Shapes/Text';
import Token from './Shapes/Token';
import SignalRing from './Shapes/SignalRing';
import SignalMeasure from './Shapes/SignalMeasure';

export default class GridController {

    constructor(ctx) {
        this.ctx = ctx;

        // View Coords
        this.width = 0;
        this.height = 0;
        this.centerX = 0;
        this.centerY = 0;
        this.viewCenter = [0,0];
        this.tileSize = 70;
        this.tileCenter = 35;

        // World Coords
        this.lookingAt = [0,0];

        // State
        this.zoomLevel = 7;
        this.shapes = [];
        this.privateShapes = [];
        this.publicTokensIds = [];
        this.privateTokensIds = [];
        this.signals = [];
        this.measures = [];
        this.currentMeasure = null;
        this.addShape = null;
        this.lastInsertedShape = null;
        this.selectedShapes = [];
        this.selectedShapesObj = [];
        this.selectionBounds = [[0,0],[0,0]];
        this.canMoveToken = null;
        this.movingToken = null;
        this.hasPropertyToChange = null;
        this.isMovingProp = false;
        this.hasElementHover = false;
        this.isMovingElement = false;
        this.isChangingToSelection = false;
        this.isOwner = false;
        this.playerId = '';
        this.playerColor = '#fff';
        this.layers = [];
        this.selectedLayer = '';
        this.selectedChar = '';
        this.gridType = 'square';
        this.gridMeasureType = 'precise';
        this.gridScale = 1.5;
        this.gridUnit = 'm';

        // Internal
        this._fpsTime = 0;
        this._fpsCount = 0;
        this._gridMode = new GridMode();
        this._gmMode = false;
        this._canvas2d = document.querySelector('#canvas2d');
        this._eventHandler = document.querySelector('#eventHandler');

        this.toolMode = '';
        this.snapMode = 'corner';
        this.toolProps = new ToolProps();
        this.characters = [];
        this.hasFocus = false;
        this.mouse = new Mouse();
        this.network = null;
        this.triggerSelectShape = () => {};
        this.triggerDrawShape = () => {};
        this.triggerImportLayers = () => {};
        this.triggerSelectToken = () => {};
        this.triggerZoomChange = () => {};
        this.triggerCenterChange = () => {};
        this.triggerTokenChange = () => {};

        this._leftKey = () => {};
        this._rightKey = () => {};
        this._upKey = () => {};
        this._downKey = () => {};
    }

    setWidth(w) {
        this.width = w;
        this.centerX = w/2;
        this.viewCenter[0] = w/2;
        this.ctx.canvas.width = w;
        this._gridMode.setWidth(w);
    }

    setHeight(h) {
        this.height = h;
        this.centerY = h/2;
        this.viewCenter[1] = h/2;
        this.ctx.canvas.height = h;
        this._gridMode.setHeight(h);
    }

    setTileSize() {
        this.tileSize = this.zoomLevel * 10;
        this.tileCenter = this.tileCenter / 2;
        this._gridMode.setTileSize(this.tileSize);
        this.mouse.setTileSize(this.tileSize);
    }

    setToolMode(tool) {
        this.toolMode = tool;
        if (tool !== Operations.SELECT) {
            this.selectedShapes = [];
            this.selectedShapesObj = [];
            this.triggerSelectShape([]);
        }
    }

    setSnapMode(snap) {
        this.snapMode = snap;
    }

    setToolProps(props) {
        this.toolProps.set(props);
        if (typeof props.gmMode !== 'undefined') {
            this._gmMode = props.gmMode;
        }

        if (this.selectedShapes.length >= 1) {
            let shapeList = !this._gmMode ? this.shapes : this.privateShapes;
            for (let shape of shapeList) {
                if (this.selectedShapes.indexOf(shape.id) >= 0) {
                    shape.setProps(this.toolProps);

                    if(shape.isToken()) {
                        this.updateTokenData();
                    }
                }
            }
        }
    }

    setCharacterList(charlist) {
        this.characters = charlist;
        this.updateTokenData();
    }

    setNetwork(network) {
        this.network = network;
    }

    setIsOwner(isOwner) {
        this.isOwner = isOwner;
    }

    setPlayerColor(color) {
        this.playerColor = color;
    }

    setPlayerId(id) {
        this.playerId = id;
    }

    setLayers(layers, selectedLayer) {
        this.selectedLayer = selectedLayer;

        for(let shape of this.selectedShapesObj) {
            shape.setLayer(selectedLayer);
        }

        let hasLayersDiff = false;
        for (let i in layers) {
            hasLayersDiff = (!layers[i] || !this.layers[i] ||
                String(layers[i].id) !== String(this.layers[i].id) ||
                String(layers[i].name) !== String(this.layers[i].name)
            );
            if (hasLayersDiff) {
                break;
            }
        }

        if (hasLayersDiff) {
            this.layers = layers;
            this.network.persistUpdate();
        }
    }

    setSelectedChar(char) {
        this.selectedChar = char;
    }

    setFocusOnCanvas(hasFocus) {
        this.hasFocus = hasFocus;
    }

    setPropsGrid(props) {
        this.gridType = props && props.gridType ? props.gridType : 'square';

        this.gridScale = props.gridScale;
        this.gridUnit = props.gridUnit;
        this.gridMeasureType = props.gridMeasureType;

        this._gridMode.setMode(this.gridType);
        Coords.setMode(this.gridType);
    }

    incrementLookingAt(x,y, distort) {
        if (this.gridType === 'hexagon') {
            const d = distort ? PixelToHexVector[2][0] : PixelToHexVector[2][1];
            this.lookingAt[0] += 2*x*PixelToHexVector[0][0];
            this.lookingAt[1] += 2*(x*PixelToHexVector[1][0] + y*d);
        } else {
            this.lookingAt[0] += x;
            this.lookingAt[1] += y;
        }

        this.triggerCenterChange(this.lookingAt);
    }

    onChangeSelection(fn) {
        this.triggerSelectShape = fn;
    }

    onDrawShape(fn) {
        this.triggerDrawShape = fn;
    }

    onChangeLayer(fn) {
        this.triggerImportLayers = fn;
    }

    onSelectToken(fn) {
        this.triggerSelectToken = fn;
    }

    onZoomChange(fn) {
        this.triggerZoomChange = fn;
    }

    onCenterChange(fn) {
        this.triggerCenterChange = fn;
    }

    onTokensChange(fn) {
        this.triggerTokenChange = fn;
    }

    manageScroll(e) {
        e.preventDefault();
        const senseY = e.deltaY / Math.abs(e.deltaY || 1);
        const senseX = e.deltaX / Math.abs(e.deltaX || 1);
        const isHorizontal = e.shiftKey || false;
        const isZoom = e.ctrlKey || false;

        if (isZoom) {
            if (senseY > 0) {
                this.zoomOut();
            } else if (senseY < 0) {
                this.zoomIn();
            }
        } else {
            if (senseX !== 0) {
                this.incrementLookingAt(0.5 * senseX, 0);
            }

            if (senseY !== 0) {
                if (isHorizontal) {
                    this.incrementLookingAt(0.5 * senseY, 0);
                } else {
                    this.incrementLookingAt(0, 0.5 * senseY, true);
                }
            }
        }
    }

    getInsertedItems() {
        if (!this.lastInsertedShape) {
            return null;
        }
        
        return [this.lastInsertedShape.export()];
    }

    getUpdatedItems() {
        let shapes = this.selectedShapesObj.map(i => i.export());
        let tokens = this.movingToken ? [this.movingToken.export()] : [];

        return [...shapes, ...tokens];
    }

    getPreviousLayer() {
        return this._gmMode ? 'publicLayer' : 'privateLayer';
    }

    getCurrentLayer() {
        return this._gmMode ? 'privateLayer' : 'publicLayer';
    }

    updateTokenData() {
        let shouldPropagate = false;

        const filterTokens = (shape) => {
            if (!(shape instanceof Token)) {
                return true;
            }

            let isOk = shape.setTokenData(this.characters);

            if (!isOk) {
                if (this.isOwner) {
                    shouldPropagate = true;
                }
                return false;
            }

            return true;
        }

        this.shapes = this.shapes.filter(shape => filterTokens(shape));
        this.privateShapes = this.privateShapes.filter(shape => filterTokens(shape));

        if (shouldPropagate) {
            this.network.persistUpdate();
            this.network.propagateUpdate();
        }
    }

    resize() {
        const size = this.ctx.canvas.getBoundingClientRect();
        this.setWidth(size.width);
        this.setHeight(size.height);

        let ratio = window.devicePixelRatio;
        this.ctx.canvas.width = size.width * ratio;
        this.ctx.canvas.height = size.height * ratio;
        this.ctx.scale(ratio, ratio);
    }

    zoomOut() {
        if (this.zoomLevel > 2) {
            this.zoomLevel--;
            this.setTileSize();
            this.triggerZoomChange(this.zoomLevel);
        }
    }

    zoomIn() {
        if (this.zoomLevel < 16) {
            this.zoomLevel++;
            this.setTileSize();
            this.triggerZoomChange(this.zoomLevel);
        }
    }

    zoomTo(value) {
        if (value < 17 && value > 1) {
            this.zoomLevel = value;
            this.setTileSize();
            this.triggerZoomChange(this.zoomLevel);
        }
    }

    jumpToCenter() {
        this.lookingAt = [0,0];
        this.triggerCenterChange(this.lookingAt);
    }

    pan(e) {
        let delta = this.mouse.getDeltaPos();
        this.incrementLookingAt(-delta[0]/this.tileSize, -delta[1]/this.tileSize);
    }

    init() {
        let findAndMove = (x,y) => {
            let shapes = [...this.shapes, ...this.privateShapes];
            for(let shape of shapes) {
                if (shape instanceof Token && shape.character && shape.character.id === this.selectedChar) {
                    shape.move(x, y);
                    this.movingToken = shape;
                    this.network.propagateUpdate();
                    this.network.persistUpdate();
                    this.movingToken = null;
                    break;
                }
            }
        };

        this._leftKey = () => {
            if (this.selectedChar && this.hasFocus) {
                findAndMove(-1, 0);
            }
            return true;
        }

        this._rightKey = () => {
            if (this.selectedChar && this.hasFocus) {
                findAndMove(1, 0);
            }
            return true;
        }

        this._upKey = () => {
            if (this.selectedChar && this.hasFocus) {
                findAndMove(0, -1);
            }
            return true;
        }

        this._downKey = () => {
            if (this.selectedChar && this.hasFocus) {
                findAndMove(0, 1);
            }
            return true;
        }

        this._escapeKey = () => {
            if (this.selectedChar) {
                this.selectedChar = null;
                this.triggerSelectToken(null);
            }
            return true;
        }


        Keyboard.register({key:'ARROWLEFT'}, this._leftKey);
        Keyboard.register({key:'ARROWRIGHT'}, this._rightKey);
        Keyboard.register({key:'ARROWUP'}, this._upKey);
        Keyboard.register({key:'ARROWDOWN'}, this._downKey);
        Keyboard.register({key:'ESCAPE'}, this._escapeKey);
    }

    clear() {
        Keyboard.unregister({key:'ARROWLEFT'}, this._leftKey);
        Keyboard.unregister({key:'ARROWRIGHT'}, this._rightKey);
        Keyboard.unregister({key:'ARROWUP'}, this._upKey);
        Keyboard.unregister({key:'ARROWDOWN'}, this._downKey);
        Keyboard.unregister({key:'ESCAPE'}, this._escapeKey);
    }

    clearGrid() {
        this.shapes = [];
        this.privateShapes = [];
    }

    jumpToToken(charId) {
        const shape = this.shapes.find(s => s instanceof Token && s.character && s.character.id === charId);

        if (shape) {
            this.lookingAt = [...shape.point];
            this.triggerCenterChange(this.lookingAt);
        }
    }

    findIfMovingToken() {
        if (!this.currentMeasure) {
            this.movingToken = this.canMoveToken;
        }
    }
    
    findIfCanMoveToken(e) {
        let worldPoint = this.mouse.getWorldPoint();
        let shapeList = !this._gmMode ? this.shapes : this.privateShapes;

        this.canMoveToken = null;

        const blockMoveToken = (e.altKey && e.shiftKey);

        if (!blockMoveToken) {
            for (let shape of shapeList) {
                if (!(shape instanceof Token)) {
                    continue;
                }

                if (shape.isHover(worldPoint)) {
                    if (this.isOwner || this.playerId === shape.getPlayerOwner()) {
                        this.canMoveToken = shape;
                    }
                }
            }
        }

        if (this.canMoveToken && !this._eventHandler.classList.contains('cursorMoveToken')) {
            this._eventHandler.classList.add('cursorMoveToken');
        } else if (!this.canMoveToken && this._eventHandler.classList.contains('cursorMoveToken')) {
            this._eventHandler.classList.remove('cursorMoveToken');
        }
    }

    findIfIsHoverPoint(e) {
        let mousePoint = this.mouse.getRealPoint();
        let shapeList = !this._gmMode ? this.shapes : this.privateShapes;

        let props = {
            viewCenter: this.viewCenter,
            lookingAt: this.lookingAt,
            tileSize: this.tileSize
        }

        this.hasPropertyToChange = null;

        for (let shape of shapeList) {
            if (this.selectedShapes.indexOf(shape.id) >= 0) {
                let prop = shape.isHoverPoint(mousePoint, props);
                if (prop) {
                    this.hasPropertyToChange = [shape, prop];
                }
            }
        }

        if (this.hasPropertyToChange && !this._eventHandler.classList.contains('cursorResize')) {
            this._eventHandler.classList.add('cursorResize');
        } else if (!this.hasPropertyToChange && this._eventHandler.classList.contains('cursorResize')) {
            this._eventHandler.classList.remove('cursorResize');
        }
    }

    findIfIsHoverElement(e) {
        let mousePoint = this.mouse.getWorldPoint();
        let snapPoint = this.mouse.getSnapPoint();
        let shapeList = !this._gmMode ? this.shapes : this.privateShapes;

        this.hasElementHover = null;

        for (let shape of shapeList) {
            if (this.selectedShapes.indexOf(shape.id) >= 0) {
                if (shape.isHover(mousePoint)) {
                    if (!this.hasElementHover) {
                        this.hasElementHover = [];
                    }
                    this.hasElementHover.push([shape, snapPoint, shape.getMoveableProps()]);
                }
            }
        }

        if (this.hasElementHover && !this._eventHandler.classList.contains('cursorMove')) {
            this._eventHandler.classList.add('cursorMove');
        } else if (!this.hasElementHover && this._eventHandler.classList.contains('cursorMove')) {
            this._eventHandler.classList.remove('cursorMove');
        }
    }

    factoryShape(rawShape) {
        let shape = null;
        let dataShape = {...rawShape};

        switch(rawShape.type) {
            case 'rectangle': shape = new Rectangle(); break;
            case 'ellipse': shape = new Ellipse(); break;
            case 'polygon': shape = new Polygon(); break;
            case 'image': shape = new ImageDraw(); break;
            case 'text': shape = new Text(); break;
            case 'token': shape = new Token(); break;
            default: break;
        }

        delete dataShape.type;
        shape.load(dataShape);
        shape.setContext(this.ctx);

        if (shape instanceof Token) {
            let isOk = shape.setTokenData(this.characters);
            if (!isOk) {
                return false;
            }
        }

        return shape;
    }

    removeSelected() {
        if (!this.isOwner) {
            return;
        }
        this.shapes = this.shapes.filter(s => this.selectedShapes.indexOf(s.id) < 0);
        this.privateShapes = this.privateShapes.filter(s => this.selectedShapes.indexOf(s.id) < 0);
        
        this.publicTokensIds = this.publicTokensIds.filter(t => this.selectedShapes.indexOf(t.id) < 0);
        this.privateTokensIds = this.privateTokensIds.filter(t => this.selectedShapes.indexOf(t.id) < 0);
        
        this.selectedShapes = [];

        this.triggerTokenChange({
            public: this.publicTokensIds,
            private: this.privateTokensIds,
        });

        this.triggerSelectShape([]);

        this.network.persistDelete();
        this.network.propagateDelete();
    }

    changePrivateLayerSelected() {
        if (!this.isOwner) {
            return;
        }

        let from = this._gmMode ? this.privateShapes : this.shapes;
        let to = this._gmMode ? this.shapes : this.privateShapes;

        for (let shape of from) {
            if (this.selectedShapes.indexOf(shape.id) >= 0) {
                to.push(shape);
            }
        }

        if (this._gmMode) {
            this.privateShapes = this.privateShapes.filter(s => this.selectedShapes.indexOf(s.id) < 0);
        } else {
            this.shapes = this.shapes.filter(s => this.selectedShapes.indexOf(s.id) < 0);
        }

        this.network.persistChangeLayer();
        this.network.propagateChangeLayer();

        this.selectedShapes = [];
    }

    changeShapeOrder(toFront = true) {
        let shapes = this._gmMode ? this.privateShapes : this.shapes;
        let newShapes = [];
        let movedShapes = [];

        for (let shape of shapes) {
            if (this.selectedShapes.indexOf(shape.id) >= 0) {
                movedShapes.push(shape);
            } else {
                newShapes.push(shape);
            }
        }

        if (this._gmMode) {
            this.privateShapes = toFront ? [...newShapes, ...movedShapes] : [...movedShapes, ...newShapes];
        } else {
            this.shapes = toFront ? [...newShapes, ...movedShapes] : [...movedShapes, ...newShapes];
        }

        this.network.persistChangeOrder(toFront ? -99999 : 99999);
        this.network.propagateChangeOrder(toFront ? -99999 : 99999);
    }

    receivingSignals(signals) {
        for (let data of signals) {
            let signal = new SignalRing(data[0], data[1]);
            signal.setContext(this.ctx);

            this.signals.push(signal);
        }
    }

    receivingMeasure(measure) {
        for (let m of this.measures) {
            if (m.getId() === measure.id) {
                m.updateProp('points', measure.points || [[0,0],[0,0]]);
                m.calculateDistance(this.gridType, this.gridMeasureType, this.gridScale, this.gridUnit);
                return;
            }
        }

        let newMeasure = new SignalMeasure(measure.points[0], measure.color)
        newMeasure.setId(measure.id);
        newMeasure.setContext(this.ctx);
        this.measures.push(newMeasure);
    }

    receivingMoving(moving) {
        this.lookingAt = [moving[0] ?? 0, moving[1] ?? 0];
        this.triggerCenterChange(this.lookingAt);
    }

    clearMeasure(measureId) {
        this.measures = this.measures.filter(m => m.getId() !== measureId);
    }

    addTokenByDrop(e, charId) {
        let worldCoord = this.gridType === 'hexagon' ? 
            Coords.toWorldHex([e.clientX, e.clientY], this.viewCenter, this.lookingAt, this.tileSize)
            :
            Coords.toWorld([e.clientX, e.clientY], this.viewCenter, this.lookingAt, this.tileSize);
            
        this.mouse.setRealPos(e.clientX, e.clientY);
        this.mouse.setWorldPos(worldCoord[0], worldCoord[1]);
        this.mouse.findSnapPoint(this.snapMode, this.gridType);
            
        let worldSnap = this.mouse.getSnapPoint();

        this.addShape = new Token(worldSnap);
        this.addShape.setProps({token:charId});
        this.addShape.setContext(this.ctx);
        this.addShape.setLayer(this.selectedLayer);
        this.addShape.setTokenData(this.characters);

        let hasToken = [this.shapes, this.privateShapes].map(v => v.some(s => s.props && s.props.token && s.props.token === charId));
        if (!hasToken[0] && !hasToken[1]) {
            if (!this._gmMode) {
                this.shapes.push(this.addShape);
                this.publicTokensIds.push(this.addShape);
            } else {
                this.privateShapes.push(this.addShape)
                this.privateTokensIds.push(this.addShape);
            }

            this.lastInsertedShape = this.addShape;
            
            this.network.persistInsert();
            this.network.propagateInsert();

            this.triggerTokenChange({
                public: this.publicTokensIds,
                private: this.privateTokensIds,
            });
        }

        this.addShape = null;

    }

    exportRender() {
        return {
            publicLayer: this.shapes.map(s => s.export()),
            privateLayer: this.privateShapes.map(s => s.export()),
        }
    }

    importRender(render) {
        if (!render || !render.publicLayer || typeof render.publicLayer.length === 'undefined') {
            return;
        }

        this.shapes = [];
        this.publicTokensIds = [];
        for (let rawShape of render.publicLayer) {
            let newShape = this.factoryShape(rawShape);
            if (newShape) {
                this.shapes.push(newShape)
            }
            if (rawShape.type === 'token') {
                this.publicTokensIds.push(newShape);
            }
        }

        this.privateShapes = [];
        this.privateTokensIds = [];
        for (let rawShape of render.privateLayer) {
            let newShape = this.factoryShape(rawShape);
            if (newShape) {
                this.privateShapes.push(newShape);
            }
            if (rawShape.type === 'token') {
                this.privateTokensIds.push(newShape);
            }
        }

        this.triggerTokenChange({
            public: this.publicTokensIds,
            private: this.privateTokensIds,
        });
    }

    importRenderOperetion(operation, render) {
        let layer = render.layer;
        let items = render.items;

        if (!items) {
            return;
        }

        for (let item of items) {
            let shape = this.factoryShape(item);
            
            if (operation === 'insert') {
                if (layer === 'privateLayer') {
                    this.privateShapes.push(shape);
                } else {
                    this.shapes.push(shape);
                }
            } else if (operation === 'update') {
                if (layer === 'privateLayer') {
                    this.privateShapes = this.privateShapes.map(s => s.id === shape.id ? shape : s);
                } else {
                    this.shapes = this.shapes.map(s => s.id === shape.id ? shape : s);
                }
            } else if (operation === 'delete') {
                if (layer === 'privateLayer') {
                    this.privateShapes = this.privateShapes.filter(s => s.id !== shape.id);
                } else {
                    this.shapes = this.shapes.filter(s => s.id !== shape.id);
                }
            } else if (operation === 'changeLayer') {
                if (layer === 'privateLayer') {
                    this.privateShapes = this.privateShapes.filter(s => s.id !== shape.id);
                    this.shapes.push(shape);
                    this.publicTokensIds.push(shape.id);
                    this.privateTokensIds = this.privateTokensIds.filter(t => shape.id !== t.id);
                } else {
                    this.shapes = this.shapes.filter(s => s.id !== shape.id);
                    this.privateShapes.push(shape);
                    this.publicTokensIds = this.privateTokensIds.filter(t => shape.id !== t.id);
                    this.privateTokensIds.push(shape.id);
                }

                this.triggerTokenChange({
                    public: this.publicTokensIds,
                    private: this.privateTokensIds,
                });
            } else if (operation === 'changeOrder') {
                let layers = layer === 'privateLayer' ? this.privateShapes : this.shapes;
                
                layers = layers.map(i => {
                    i.move = shape.id === i.id ? render.distance : 0; 
                    return i;
                });
                layers = layers.sort((a, b) => a.move > b.move ? -1 : (b.move > a.move ? 1 : 0));
                layers = layers.map(i => {
                    delete i.move;
                    return i;
                });

                if (layer === 'privateLayer') {
                    this.privateShapes = layers;
                } else {
                    this.shapes = layers;
                }
            }
        }
    }

    exportLayers() {
        return this.layers;
    }

    importLayers(layers) {
        this.layers = layers;
        this.triggerImportLayers(layers);
    }

    mouseDown(e) {
        let worldSnap = this.mouse.getSnapPoint();
        let worldPoint = this.mouse.getWorldPoint();

        this.mouse.setButtonState(e.button, true);
        this.mouse.setRealPos(e.clientX, e.clientY);

        if (e.button === Mouse.RIGHT) {
            e.preventDefault();
        } 

        if (e.button === Mouse.LEFT) {

            if (e.altKey && e.shiftKey) {
                let line = new SignalMeasure(worldSnap, this.playerColor);
                line.setContext(this.ctx);

                this.currentMeasure = line;
            }

            switch(this.toolMode) {
                case Operations.DRAW_RECTANGLE:
                    this.addShape = new Rectangle(worldSnap, worldSnap);
                    break;
                case Operations.DRAW_ELLIPSE:
                    this.addShape = new Ellipse(worldSnap, worldSnap);
                    break;
                case Operations.DRAW_IMAGE:
                    this.addShape = new ImageDraw(worldSnap, worldSnap);
                    break;
                case Operations.DRAW_POLYGON:
                    if (this.addShape === null) {
                        this.addShape = new Polygon(worldSnap);
                    } else {
                        this.addShape.checkIsClosingPath();
                        if (!this.addShape.isClosed) {
                            this.addShape.updateProp('addPoint', worldSnap);    
                        }
                    }
                    break;
                case Operations.DRAW_TEXT:
                    this.addShape = new Text(worldSnap);
                    break;
                case Operations.DRAW_TOKEN:
                    this.addShape = new Token(worldSnap);
                    break;
                case Operations.SELECT:
                    this.isMovingProp = this.hasPropertyToChange;
                    if (this.hasElementHover) {
                        this.isMovingElement = [];
                        let shapeList = !this._gmMode ? this.shapes : this.privateShapes;

                        for (let shape of shapeList) {
                            if (this.selectedShapes.indexOf(shape.id) >= 0) {
                                this.isMovingElement.push([shape, worldSnap, shape.getMoveableProps()]);
                            }
                        }
                    }
                    if (!this.hasPropertyToChange) {
                        this.selectionBounds[0] = [...worldPoint];
                        this.selectionBounds[1] = [...worldPoint];
                    }
                    break;
                case 'none':
                    this.findIfMovingToken();
                    break;
                default:
                    break;
            }

            if (this.addShape !== null) {
                this.addShape.setProps(this.toolProps);
                this.addShape.setContext(this.ctx);
                this.addShape.setLayer(this.selectedLayer);

                if (this.addShape instanceof Token) {
                    this.addShape.setTokenData(this.characters);

                    if(this._gmMode) {
                        this.privateTokensIds.push(this.addShape);
                    } else {
                        this.publicTokensIds.push(this.addShape);
                    }

                    this.triggerTokenChange({
                        public: this.publicTokensIds,
                        private: this.privateTokensIds,
                    });
                }
            }
        }
    }

    mouseMove(e) {
        let worldCoord = this.gridType === 'hexagon' ? 
            Coords.toWorldHex([e.clientX, e.clientY], this.viewCenter, this.lookingAt, this.tileSize)
            :
            Coords.toWorld([e.clientX, e.clientY], this.viewCenter, this.lookingAt, this.tileSize);
        let worldSnap = this.mouse.getSnapPoint();

        this.mouse.setRealPos(e.clientX, e.clientY);
        this.mouse.setWorldPos(worldCoord[0], worldCoord[1]);

        if (this._canvas2d.tagName && this._canvas2d.tagName.toUpperCase() === 'CANVAS') {
            this.findIfCanMoveToken(e);
            this.findIfIsHoverPoint(e);
            this.findIfIsHoverElement(e);
        }

        if (this.currentMeasure) {
            this.currentMeasure.updateProp('point2', worldSnap);
            this.currentMeasure.calculateDistance(this.gridType, this.gridMeasureType, this.gridScale, this.gridUnit);
            if (!e.ctrlKey) {
                this.network.sendMeasure(this.currentMeasure);
            }
        }

        if (this.mouse.isButtonPressed(Mouse.RIGHT)) {
            this.pan(e);

            if (e.shiftKey && this.isOwner) {
                this.network.sendMoving(this.lookingAt);
            }
        }

        if (this.mouse.isButtonPressed(Mouse.LEFT)) {
            switch(this.toolMode) {
                case Operations.DRAW_RECTANGLE:
                case Operations.DRAW_ELLIPSE:
                case Operations.DRAW_IMAGE:
                    this.addShape.updateProp('point2', worldSnap);
                    break;
                case Operations.DRAW_TEXT:
                case Operations.DRAW_TOKEN:
                    this.addShape.updateProp('point', worldSnap);
                    break;
                case Operations.SELECT:
                    if (this.isMovingProp) {
                        let prop = this.isMovingProp[1];
                        this.isMovingProp[0].updateProp(prop, worldSnap);
                    } else if (this.isMovingElement) {
                        this.isMovingElement.map(i => i[0].relativeMove(worldSnap, i[1], i[2]));
                    } else {
                        this.selectionBounds[1] = [...worldCoord];
                    } 
                    break;
                case 'none':
                    if (this.movingToken) {
                        this.movingToken.updateProp('point', worldSnap);
                    }
                    break;
                default:
                    break;
            }
        } else {
            if (this.addShape !== null && this.toolMode === Operations.DRAW_POLYGON) {
                this.addShape.updateProp('lastPoint', worldSnap);
            }
        }

        this.mouse.findSnapPoint(this.snapMode, this.gridType);
    }

    mouseUp(e) {
        let worldPoint = this.mouse.getWorldPoint();
        this.mouse.setButtonState(e.button, false);

        if (e.button === Mouse.LEFT) {
            if (this.addShape !== null) {
                switch(this.toolMode) {
                    case Operations.DRAW_RECTANGLE:
                    case Operations.DRAW_ELLIPSE:
                    case Operations.DRAW_IMAGE:
                    case Operations.DRAW_TEXT:
                    case Operations.DRAW_TOKEN:
                        if (!this._gmMode) {
                            this.shapes.push(this.addShape);
                        } else {
                            this.privateShapes.push(this.addShape)
                        }
                        this.selectedShapes = [this.addShape.id];
                        this.selectedShapesObj = [this.addShape];
                        this.triggerDrawShape();
                        this.triggerSelectShape(this.selectedShapesObj);
                        this.isChangingToSelection = true;
                        this.lastInsertedShape = this.addShape;
                        this.addShape = null;
                        break;
                    case Operations.DRAW_POLYGON:
                        if (this.addShape.isClosed) {
                            if (!this._gmMode) {
                                this.shapes.push(this.addShape);
                            } else {
                                this.privateShapes.push(this.addShape)
                            }

                            this.selectedShapes = [this.addShape.id];
                            this.selectedShapesObj = [this.addShape];
                            this.triggerDrawShape();
                            this.triggerSelectShape(this.selectedShapesObj);
                            this.isChangingToSelection = true;
                            
                            this.lastInsertedShape = this.addShape;
                            this.addShape = null;
                        }
                        break;
                    default:
                        break;
                }

                this.network.persistInsert();
                this.network.propagateInsert();
            }

            if (this.movingToken || this.isMovingProp || this.isMovingElement) {
                this.triggerSelectToken(this.movingToken && this.movingToken.character ? this.movingToken.character.id : null);

                this.network.persistUpdate();
                this.network.propagateUpdate();
            }

            if (this.toolMode === Operations.SELECT && !this.isMovingProp && !this.isMovingElement && !this.isChangingToSelection) {
                if (e.target.id === 'eventHandler') {
                    let multiSelect = e.ctrlKey;
                    let shapeList = !this._gmMode ? this.shapes : this.privateShapes;

                    if (!multiSelect) {
                        this.selectedShapes = [];
                        this.selectedShapesObj = [];
                    }

                    let shouldSkipNext = true;
                    let isMassiveSelection = false;
                    for (let i=shapeList.length-1; i>=0; i--) {
                        let shape = shapeList[i];
                        let shouldSelect = false;

                        if (shape.layer === this.selectedLayer) {
                            if (this.selectionBounds[0][0] !== this.selectionBounds[1][0] && 
                                this.selectionBounds[0][1] !== this.selectionBounds[1][1]) {

                                isMassiveSelection = true;
                                shouldSkipNext = false;

                                if (shape.isUnderSelection(this.selectionBounds)) {
                                    shouldSelect = true;
                                }
                            } else if (shape.isHover(worldPoint)) {
                                shouldSelect = true;
                            }
                        }

                        if (shouldSelect) {
                            if (!multiSelect && !isMassiveSelection) {
                                this.selectedShapes = [shape.id];
                                this.selectedShapesObj = [shape];
                            } else {
                                let index = this.selectedShapes.indexOf(shape.id);
                                if (index < 0) {
                                    this.selectedShapes.push(shape.id);
                                    this.selectedShapesObj.push(shape);
                                } else {
                                    this.selectedShapes.splice(index, 1);
                                    this.selectedShapesObj.splice(index, 1);
                                }
                            }
                            if (shouldSkipNext) {
                                break;
                            }
                        }
                    }

                    this.triggerSelectShape(this.selectedShapesObj);
                }

                this.selectionBounds = [[0, 0], [0, 0]];
            }

            this.isChangingToSelection = false;
            this.isMovingProp = false;
            this.isMovingElement = false;

            if (e.altKey && !e.shiftKey) {
                let ring = new SignalRing(worldPoint, this.playerColor);
                ring.setContext(this.ctx);

                this.signals.push(ring);
                this.network.sendSignal(worldPoint, this.playerColor);
            }

            if (this.currentMeasure) {
                this.network.clearMeasure(this.currentMeasure.id);
                this.currentMeasure = null;
            }

            this.movingToken = null;
        }
    }

    draw() {
        this.ctx.clearRect(0, 0, this.width, this.height);
        
        let props = {
            viewCenter: this.viewCenter,
            lookingAt: this.lookingAt,
            tileSize: this.tileSize,
            hover: false,
            selectedChar: this.selectedChar,
            gridType: this.gridType,
            isOwner: this.isOwner
        }

        for(let shape of this.shapes) {
            props.hover = this.selectedShapes.indexOf(shape.id) >= 0;
            shape.draw(props);
        }

        if (this._gmMode) {
            this.ctx.fillStyle = 'rgba(0,0,0,0.7)';
            this.ctx.fillRect(0, 0, this.width, this.height);

            for (let shape of this.privateShapes) {
                props.hover = this.selectedShapes.indexOf(shape.id) >= 0;
                shape.draw(props);
            }
        }

        if (this.addShape !== null) {
            this.addShape.draw(props);
        }

        for (let signal of this.signals) {
            signal.draw(props);
        }
        this.signals = this.signals.filter(s => s.alive());

        for (let measure of this.measures) {
            if (measure) {
                measure.draw(props);
            }
        }

        if (this.currentMeasure) {
            this.currentMeasure.draw(props);
        }

        if (this.selectionBounds[0][0] !== this.selectionBounds[1][0] && 
            this.selectionBounds[0][1] !== this.selectionBounds[1][1]) {

            let selection1 = Coords.toView(this.selectionBounds[0], this.viewCenter, this.lookingAt, this.tileSize);
            let selection2 = Coords.toView(this.selectionBounds[1], this.viewCenter, this.lookingAt, this.tileSize);
            this.ctx.fillStyle = 'rgba(0, 50, 200, 0.5)';
            this.ctx.strokeStyle = 'rgba(0, 50, 200, 0.8)'
            this.ctx.fillRect(selection1[0], selection1[1], selection2[0] - selection1[0], selection2[1] - selection1[1]);
        }

        this.drawMouseLocation();
        this.drawGrid();
        this.drawFPS();
    }

    drawGrid() {
        this._gridMode.setLookingAt(this.lookingAt);
        this._gridMode.draw(this.ctx);
    }

    drawMouseLocation() {
        let centerPointer = Coords.toView(this.mouse.getSnapPoint(), this.viewCenter, this.lookingAt, this.tileSize);
        
        this.ctx.strokeStyle = "#0f0";
        this.ctx.lineWidth = 1;

        this.ctx.beginPath();
        this.ctx.arc(centerPointer[0], centerPointer[1], 1, 0, 6.283185307179586);
        this.ctx.stroke();
    }

    drawFPS() {
        let dt = (new Date()).getTime();
        this._fpsTime = dt - this._fpsCount;
        this._fpsTime = this._fpsTime > 0 ? this._fpsTime : -1000;
        this._fpsCount = dt;
        this.ctx.font = '14px sans-serif';
        this.ctx.fillStyle = '#ff0';
        this.ctx.fillText(Math.round(1000/this._fpsTime), 0, 15);
        //this.ctx.fillText(this.shapes.length + '/' + this.privateShapes.length, this.width - 30, this.height - 35);
    }


}